import { notifyFeedbackActionCompleted } from '../utils/feedbackEvents';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useT, useLocale } from '../i18n/LanguageContext';
import { captureSession, isSessionCurrent, SESSION_EVENT } from '../utils/authSession';
import PageHeader from '../components/PageHeader';
import ResourceDialog, { ResourceMedia } from '../components/resources/ResourceDialog';
import ResourceEditor from '../components/resources/ResourceEditor';
import ProductSizing from '../components/resources/ProductSizing';
import service, {
  resourceError,
  resourceOperationId,
  verifiedResourceBlob,
} from '../services/resourceCenterService';
import {
  buildResourceSearchRequest,
  resourceFileKind,
  resourceStateLabel,
} from '../features/resources/resourceList';
import useResourcePreview from '../features/resources/useResourcePreview';
import ResourceTabs, { ResourceStateTabs } from '../features/resources/ResourceTabs';
import './ResourceCenter.css';
function ResourceWorkspace({ user, distributorId, onLockChange }) {
  const scopeArgs = useMemo(() => (distributorId ? [distributorId] : []), [distributorId]);
  const t = useT();
  const locale = useLocale();
  const admin = ['admin', 'master_distributor'].includes(user.role);
  const scoped = distributorId || user.distributor || user.role === 'master_distributor';
  const readOnly = Boolean(user.distributorPreviewExpiresAt);
  const [tab, setTab] = useState('documents');
  const [manage, setManage] = useState(admin);
  const [state, setState] = useState('all');
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [cursors, setCursors] = useState([null]);
  const [nextCursor, setNextCursor] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [revision, setRevision] = useState(0);
  const [editor, setEditor] = useState(undefined);
  const { preview, showPreview, closePreview, discardPreview, downloadFile, discardDownloads } =
    useResourcePreview();
  const [confirmation, setConfirmation] = useState(null);
  const [notice, setNotice] = useState('');
  const [ended, setEnded] = useState(false);
  const session = useRef(captureSession());
  const alive = useRef(true);
  const listRequest = useRef(0);
  const actionLock = useRef(false);
  const modalTrigger = useRef(null);
  const valid = useCallback(() => alive.current && isSessionCurrent(session.current), []);
  useEffect(() => {
    alive.current = true;
    const changed = () => {
      if (!isSessionCurrent(session.current)) {
        discardPreview();
        discardDownloads();
        setEnded(true);
      }
    };
    window.addEventListener(SESSION_EVENT, changed);
    window.addEventListener('storage', changed);
    return () => {
      alive.current = false;
      discardPreview();

      window.removeEventListener(SESSION_EVENT, changed);
      window.removeEventListener('storage', changed);
    };
  }, [discardPreview, discardDownloads]);
  const loadConfig = useCallback(async () => {
    setConfigError(false);
    try {
      const next = await service.config(...scopeArgs);
      if (valid())
        setConfig(readOnly ? { ...next, writesEnabled: false, uploadsEnabled: false } : next);
    } catch {
      if (valid()) setConfigError(true);
    }
  }, [valid, scopeArgs, readOnly]);
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);
  useEffect(() => {
    if (searchText === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(searchText);
      setPage(0);
      setCursors([null]);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchText, search]);
  useEffect(() => {
    if (!config?.enabled) return;
    const request = ++listRequest.current;
    setLoading(true);
    setListError('');
    service
      .search(
        buildResourceSearchRequest({ search, category, manage, state, cursor: cursors[page] }),
        manage,
        ...scopeArgs
      )
      .then((result) => {
        if (valid() && request === listRequest.current) {
          setRows(result.items);
          setTotal(result.total);
          setNextCursor(result.nextCursor);
        }
      })
      .catch(() => {
        if (valid() && request === listRequest.current) {
          setRows([]);
          setListError(t('Could not load documents. Please retry.'));
        }
      })
      .finally(() => {
        if (valid() && request === listRequest.current) setLoading(false);
      });
  }, [config, search, category, manage, state, cursors, page, revision, t, valid, scopeArgs]);
  const resetPage = () => {
    setPage(0);
    setCursors([null]);
  };
  const refresh = () => {
    resetPage();
    setRevision((value) => value + 1);
  };
  const file = async (row, download) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setBusy(row.id);
    setError('');
    try {
      const response = await service.file(row.id, ...scopeArgs);
      if (!valid()) return;
      const blob = verifiedResourceBlob(response, row);
      if (download) {
        downloadFile(blob, row.fileName);
        notifyFeedbackActionCompleted();
      } else {
        showPreview(row, blob);
      }
    } catch (failure) {
      if (valid()) setError(t(resourceError(failure)));
    } finally {
      actionLock.current = false;
      if (valid()) setBusy('');
    }
  };
  const edit = async (row) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setBusy(row.id);
    setError('');
    try {
      const next = await service.get(row.id, true, ...scopeArgs);
      if (valid()) setEditor(next);
    } catch (failure) {
      if (valid()) setError(t(resourceError(failure)));
    } finally {
      actionLock.current = false;
      if (valid()) setBusy('');
    }
  };
  const transition = async () => {
    if (actionLock.current || !confirmation) return;
    actionLock.current = true;
    setBusy(confirmation.row.id);
    setError('');
    try {
      await service[confirmation.action](confirmation.row.id, confirmation.body, ...scopeArgs);
      if (valid()) {
        setNotice(
          t(
            confirmation.action === 'archive'
              ? 'Document archived. Users can no longer access it in the app.'
              : 'Document restored as a draft. Review and publish it to share again.'
          )
        );
        setConfirmation(null);
        refresh();
      }
    } catch (failure) {
      if (valid()) setError(t(resourceError(failure)));
    } finally {
      actionLock.current = false;
      if (valid()) setBusy('');
    }
  };
  useEffect(() => {
    onLockChange?.(editor !== undefined);
    return () => onLockChange?.(false);
  }, [editor, onLockChange]);
  if (ended) return null;
  return (
    <div className={`rc-page${scoped ? ' rc-distributor' : ''}`}>
      <div>
        <PageHeader
          title="Resources & Tools"
          subtitle="Product information and tools, all in one place."
        />
        <ResourceTabs
          active={tab}
          t={t}
          showBuyingPower={user.role === 'master_distributor'}
          onChange={(next) => {
            setTab(next);
            setError('');
          }}
        />
        {configError ? (
          <div className="rc-empty" role="alert">
            <p>{t('Could not load Resources & Tools.')}</p>
            <button onClick={loadConfig}>{t('Retry')}</button>
          </div>
        ) : !config ? (
          <p role="status">{t('Loading Resources & Tools…')}</p>
        ) : !config.enabled ? (
          <div className="rc-empty">{t('Resources & Tools is not available yet.')}</div>
        ) : tab === 'sizing' ? (
          <div id="rc-panel-sizing" role="tabpanel" aria-labelledby="rc-tab-sizing">
            <ProductSizing
              enabled={config.sizingEnabled}
              distributorId={distributorId}
              readOnly={readOnly}
            />
          </div>
        ) : (
          <section id="rc-panel-documents" role="tabpanel" aria-labelledby="rc-tab-documents">
            <div className="rc-document-heading">
              <h2>{t(manage ? 'Manage documents' : 'Document library')}</h2>
              {admin && (
                <div className="rc-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setManage((value) => !value);
                      setState('all');
                      resetPage();
                      setError('');
                    }}
                  >
                    {t(manage ? 'View library' : 'Manage documents')}
                  </button>
                  <button
                    className="rc-primary"
                    type="button"
                    disabled={!config.writesEnabled || !config.uploadsEnabled}
                    onClick={(event) => {
                      modalTrigger.current = event.currentTarget;
                      setEditor(null);
                    }}
                  >
                    <span aria-hidden="true">+</span>
                    {t('Add document')}
                  </button>
                </div>
              )}
            </div>
            {manage && (
              <ResourceStateTabs
                state={state}
                t={t}
                onChange={(key) => {
                  setState(key);
                  resetPage();
                }}
              />
            )}
            <div className="rc-toolbar">
              <label className="rc-search">
                {t('Search documents')}
                <input
                  type="search"
                  placeholder={t('Search titles or descriptions')}
                  value={searchText}
                  maxLength={120}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </label>
              <label>
                {t('Category')}
                <select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    resetPage();
                  }}
                >
                  <option value="">{t('All categories')}</option>
                  {config.categories.map((item) => (
                    <option key={item.id} value={item.id}>
                      {t(item.label)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {notice && (
              <p className="rc-notice" role="status">
                {notice}
              </p>
            )}
            {error && (
              <p className="rc-error" role="alert">
                {error}
              </p>
            )}
            {loading ? (
              <div className="rc-empty" role="status">
                {t('Loading documents…')}
              </div>
            ) : listError ? (
              <div className="rc-empty" role="alert">
                <p>{listError}</p>
                <button onClick={refresh}>{t('Retry')}</button>
              </div>
            ) : !rows.length ? (
              <div className="rc-empty">
                <span className="material-symbols-outlined" aria-hidden="true">
                  folder_open
                </span>
                <h3>{t(search || category ? 'No matching documents' : 'No documents here yet')}</h3>
                <p>
                  {t(
                    search || category
                      ? 'Try another search or category.'
                      : manage
                        ? 'Add a document and review it before publishing.'
                        : 'Published resources will appear here.'
                  )}
                </p>
                {(search || category) && (
                  <button
                    onClick={() => {
                      setSearchText('');
                      setSearch('');
                      setCategory('');
                      resetPage();
                    }}
                  >
                    {t('Clear filters')}
                  </button>
                )}
              </div>
            ) : (
              <>
                <ul className="rc-document-list">
                  {rows.map((row) => (
                    <li key={row.id} className="rc-document-row">
                      <span
                        className={`rc-file-icon ${row.mimeType === 'application/pdf' ? 'rc-file-pdf' : ''}`}
                        aria-hidden="true"
                      >
                        {resourceFileKind(row.mimeType)}
                      </span>
                      <div className="rc-document-copy">
                        <h3>{row.title}</h3>
                        <p className="rc-description">{row.description}</p>
                        <div className="rc-file-meta">
                          <span>
                            {t(
                              config.categories.find((item) => item.id === row.category)?.label ||
                                'Other'
                            )}
                          </span>
                          <span>{Math.max(1, Math.round(row.fileBytes / 1024))} KB</span>
                          <span>
                            {new Date(row.updatedAt).toLocaleDateString(locale, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                          {manage && <span>{t(resourceStateLabel(row.state))}</span>}
                          {manage &&
                            row.publishedRevisionId &&
                            row.draftRevisionId &&
                            row.publishedRevisionId !== row.draftRevisionId && (
                              <span>{t('Unpublished changes')}</span>
                            )}
                        </div>
                      </div>
                      <div className="rc-row-actions">
                        {manage ? (
                          <>
                            <button
                              type="button"
                              disabled={!!busy || !config.writesEnabled}
                              onClick={(event) => {
                                modalTrigger.current = event.currentTarget;
                                if (row.state === 'archived')
                                  setConfirmation({
                                    row,
                                    action: 'restore',
                                    body: {
                                      operationId: resourceOperationId(),
                                      revision: row.revision,
                                    },
                                  });
                                else edit(row);
                              }}
                            >
                              {t(row.state === 'archived' ? 'Restore' : 'Edit & preview')}
                            </button>
                            {row.state !== 'archived' && (
                              <button
                                type="button"
                                disabled={!!busy || !config.writesEnabled}
                                onClick={(event) => {
                                  modalTrigger.current = event.currentTarget;
                                  setConfirmation({
                                    row,
                                    action: 'archive',
                                    body: {
                                      operationId: resourceOperationId(),
                                      revision: row.revision,
                                    },
                                  });
                                }}
                              >
                                {t('Archive')}
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={!!busy}
                              onClick={(event) => {
                                modalTrigger.current = event.currentTarget;
                                file(row, false);
                              }}
                              aria-label={t('View {title}', { title: row.title })}
                            >
                              {t(busy === row.id ? 'Loading…' : 'View')}
                            </button>
                            <button
                              type="button"
                              className="rc-download"
                              disabled={!!busy}
                              onClick={() => file(row, true)}
                              aria-label={t('Download {title}', { title: row.title })}
                            >
                              <span className="material-symbols-outlined" aria-hidden="true">
                                download
                              </span>
                              <span>{t('Download')}</span>
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="rc-pagination">
                  <span>
                    {t(total === 1 ? '1 document' : '{count} documents', { count: total })}
                  </span>
                  <div className="rc-actions">
                    <button disabled={page === 0} onClick={() => setPage((value) => value - 1)}>
                      {t('Previous')}
                    </button>
                    <span>{t('Page {page}', { page: page + 1 })}</span>
                    <button
                      disabled={!nextCursor}
                      onClick={() => {
                        setCursors((old) => [...old.slice(0, page + 1), nextCursor]);
                        setPage((value) => value + 1);
                      }}
                    >
                      {t('Next')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        )}
      </div>
      {editor !== undefined && (
        <ResourceEditor
          distributorId={distributorId}
          distributorAudience={Boolean(distributorId || user.distributor)}
          returnFocus={modalTrigger.current}
          key={editor?.id || 'new'}
          resource={editor}
          config={config}
          onClose={() => setEditor(undefined)}
          onSaved={(next, outcome) => {
            refresh();
            if (outcome === 'reloaded') return setNotice('');
            setNotice(
              t(
                outcome === 'published'
                  ? distributorId || user.distributor
                    ? 'Document published. Users in this distributor workspace can view it.'
                    : 'Document published. All users can view it.'
                  : 'Document saved. Only published documents are shared.'
              )
            );
          }}
        />
      )}
      {preview && (
        <ResourceDialog
          returnFocus={modalTrigger.current}
          wide
          media
          title={preview.title}
          onClose={closePreview}
        >
          <div className="rc-public-preview">
            <p>{preview.description}</p>
            <ResourceMedia url={preview.url} mimeType={preview.mimeType} title={preview.title} />
            <button className="rc-primary" disabled={!!busy} onClick={() => file(preview, true)}>
              {t('Download')}
            </button>
            {error && (
              <p role="alert" className="rc-error">
                {error}
              </p>
            )}
          </div>
        </ResourceDialog>
      )}
      {confirmation && (
        <ResourceDialog
          returnFocus={modalTrigger.current}
          title={t(confirmation.action === 'archive' ? 'Archive document?' : 'Restore document?')}
          onClose={() => {
            setConfirmation(null);
            setError('');
          }}
          busy={!!busy}
        >
          <div className="rc-confirmation">
            <h3>{confirmation.row.title}</h3>
            <p>
              {t(
                confirmation.action === 'archive'
                  ? 'This removes the document from the library. Copies already downloaded cannot be recalled.'
                  : 'This returns the document to Drafts. It will not be shared until you review and publish it.'
              )}
            </p>
            {error && (
              <p role="alert" className="rc-error">
                {error}
              </p>
            )}
            <div className="rc-actions">
              <button
                disabled={!!busy}
                onClick={() => {
                  setConfirmation(null);
                  setError('');
                }}
              >
                {t('Cancel')}
              </button>
              <button className="rc-primary" disabled={!!busy} onClick={transition}>
                {t(confirmation.action === 'archive' ? 'Archive' : 'Restore to drafts')}
              </button>
            </div>
          </div>
        </ResourceDialog>
      )}
    </div>
  );
}
export default function ResourceCenter({ adminDistributorId, onLockChange }) {
  const { user, sessionVersion } = useAuth();
  if (
    !user ||
    user.isActive === false ||
    !['admin', 'sales_rep', 'clinic', 'master_distributor'].includes(user.role)
  )
    return null;
  if (adminDistributorId && (user.role !== 'admin' || !/^[a-f\d]{24}$/.test(adminDistributorId)))
    return null;
  return (
    <ResourceWorkspace
      key={`${user._id || user.id}:${user.role}:${user.distributor || ''}:${user.clinic || ''}:${sessionVersion || ''}:${adminDistributorId || 'direct'}`}
      user={user}
      distributorId={adminDistributorId}
      onLockChange={onLockChange}
    />
  );
}
